import type { FormFieldDef, AddressValue } from '@hcc/types';
import { asAddress } from '@hcc/types';
import { inputClass } from './fieldStyles';
import { FieldLabelText, FieldHelp } from './fieldChrome';
import { asArray, type FieldValue } from './formValues';

interface CompositeFieldViewProps {
  field: FormFieldDef;
  value: FieldValue;
  onChange: (next: FieldValue) => void;
  disabled?: boolean;
  err: boolean;
  /** Names of the address sub-fields that failed validation. */
  invalidParts?: string[];
}

/**
 * The multi-input composite family: name ([first, last]) and the
 * structured UK address block.
 */
export function CompositeFieldView({
  field,
  value,
  onChange,
  disabled,
  err,
  invalidParts,
}: CompositeFieldViewProps) {
  if (field.type === 'name') {
    const parts = asArray(value);
    const sub = inputClass(err, false);
    return (
      <fieldset className="block text-sm">
        <FieldLabelText field={field} />
        <div className="mt-1.5 grid grid-cols-2 gap-2">
          <input
            type="text"
            placeholder="First name"
            value={parts[0] ?? ''}
            disabled={disabled}
            onChange={(e) => onChange([e.target.value, parts[1] ?? ''])}
            className={sub}
          />
          <input
            type="text"
            placeholder="Last name"
            value={parts[1] ?? ''}
            disabled={disabled}
            onChange={(e) => onChange([parts[0] ?? '', e.target.value])}
            className={sub}
          />
        </div>
        <FieldHelp field={field} />
      </fieldset>
    );
  }

  // address
  const addr = asAddress(value);
  const set = (patch: Partial<AddressValue>) => onChange({ ...addr, ...patch });
  // A part is red if the whole field failed, or if it is named in `parts`
  // (the missing required sub-fields) - so the inductee sees exactly which
  // line of which address still needs filling in.
  const sub = (key: string) => inputClass(err || (invalidParts?.includes(key) ?? false), false);
  // Caption sits above its box, matching the label-above-input convention used
  // by every other field; below was confusing once the boxes were filled.
  const caption = 'mb-1 block text-xs text-fg-dim';
  return (
    <fieldset className="block text-sm">
      <FieldLabelText field={field} />
      <div className="mt-1.5 space-y-3">
        <div>
          <span className={caption}>1st Line of Address</span>
          <input
            type="text"
            value={addr.line1}
            disabled={disabled}
            onChange={(e) => set({ line1: e.target.value })}
            className={sub('line1')}
          />
        </div>
        <div>
          <span className={caption}>2nd Line of Address</span>
          <input
            type="text"
            value={addr.line2}
            disabled={disabled}
            onChange={(e) => set({ line2: e.target.value })}
            className={sub('line2')}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <span className={caption}>City</span>
            <input
              type="text"
              value={addr.city}
              disabled={disabled}
              onChange={(e) => set({ city: e.target.value })}
              className={sub('city')}
            />
          </div>
          <div>
            <span className={caption}>County</span>
            <input
              type="text"
              value={addr.county}
              disabled={disabled}
              onChange={(e) => set({ county: e.target.value })}
              className={sub('county')}
            />
          </div>
        </div>
        <div>
          <span className={caption}>Post Code</span>
          {/* Postcodes are uppercased as typed; UK formats vary too much for a hard mask. */}
          <input
            type="text"
            value={addr.postcode}
            disabled={disabled}
            onChange={(e) => set({ postcode: e.target.value.toUpperCase() })}
            className={sub('postcode')}
          />
        </div>
      </div>
      <FieldHelp field={field} />
    </fieldset>
  );
}
